import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ProductCountryService } from "@domain/product/product-country.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { catchError, EMPTY, filter, map, Observable, of, switchMap, tap } from "rxjs";

@Component({
  selector: "app-shared-project-link",
  imports: [],
  template: "",
  styles: "",
  standalone: true,
})
export class SharedProjectLinkComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private projectService = inject(ProjectService);
  private productCountryService = inject(ProductCountryService);
  private toastHelper = inject(ToastHelper);

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => params.get("id")),
        filter((cloudId): cloudId is string => !!cloudId),
        switchMap((cloudId) =>
          this.projectService.findLocalProjectByCloudId(cloudId).pipe(
            switchMap((localProject) => {
              const project$ = localProject ? of(localProject) : this.createProjectFromCloud(cloudId);
              return project$.pipe(
                switchMap((project) => this.projectService.updateUserWritePermission(project).pipe(map(() => project))),
              );
            }),
          ),
        ),
      )
      .subscribe((createdProject) => {
        if (createdProject) {
          this.navigateToProject(createdProject.id);
        }
      });
  }

  private navigateToProject(projectId: string): void {
    this.router.navigate(["projects", projectId]);
  }

  private createProjectFromCloud(cloudId: string): Observable<ScopedProject> {
    return this.projectService.loadProjectAndMetadataFromCloud(cloudId).pipe(
      switchMap(([project, metadata]) =>
        this.projectService
          .createProject(project, metadata)
          .pipe(tap((createdProject) => this.productCountryService.selectCountry(createdProject.country))),
      ),
      catchError(() => {
        this.toastHelper.error(
          $localize`:@@toast.project.projectShareFailed.header:Projekt konnte nicht gefunden werden`,
          $localize`:@@toast.project.projectShareFailed.description:Das Projekt konnte nicht geöffnet werden. Es ist möglicherweise noch nicht synchronisiert worden.`,
        );
        this.router.navigate([""]);
        return EMPTY;
      }),
    );
  }
}
